import { useEffect, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { sitesApi } from "../../services/sitesApi";
import { openPrintWindow } from "../../utils/printWindow";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import { WatermarkSelect } from "./WatermarkSelect";

/** "Print Site Report": all hives at a site with a status summary. Site
 * selection depends on sitesApi.list() -- see that file's header comment
 * for the backend gap it's waiting on; degrades to an empty picker rather
 * than crashing if that endpoint isn't available yet. */
export function PrintSiteReport({ trigger, open: openProp, onOpenChange }) {
  // Optionally CONTROLLED (#327). It owned its open state and rendered
  // its own trigger, which was fine while the trigger was a button in a
  // row -- but rule 6 moved it into an overflow menu, and a menu item
  // cannot click a button it does not render.
  //
  // Falls back to internal state when no `open` is passed, so the
  // existing trigger form keeps working untouched.
  const [openSelf, setOpenSelf] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openSelf;
  const setOpen = (v) => (controlled ? onOpenChange?.(v) : setOpenSelf(v));
  const [sites, setSites] = useState(null);
  const [siteId, setSiteId] = useState("");
  const [watermark, setWatermark] = useState("None");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && sites === null) {
      sitesApi.list().then((r) => setSites(r.sites)).catch(() => setSites([]));
    }
  }, [open, sites]);

  async function print() {
    if (!siteId) return;
    setBusy(true);
    try {
      const site = sites.find((s) => String(s.site_id) === String(siteId));
      const { hives } = await hivesApi.list({ site_id: Number(siteId) });

      const statusCounts = hives.reduce((acc, h) => {
        acc[h.status] = (acc[h.status] || 0) + 1;
        return acc;
      }, {});

      const summaryCards = Object.entries(statusCounts)
        .map(([status, count]) => `<div class="summary-card"><div class="value">${count}</div><div class="label">${status}</div></div>`)
        .join("");

      const rows = hives
        .map(
          (h) => `<tr>
            <td>${escapeHtml(h.hive_number)}</td>
            <td>${escapeHtml(h.status)}</td>
            <td>${escapeHtml(h.hive_type)}</td>
            <td>${h.last_checked ? new Date(h.last_checked).toLocaleDateString() : "Never"}</td>
          </tr>`
        )
        .join("");

      const body = `
        <h1>${escapeHtml(site?.dock_name || "Site")} Report</h1>
        <div class="meta">${hives.length} hive(s) · Generated ${new Date().toLocaleDateString()}</div>
        <div class="summary-grid">${summaryCards || "<p>No hives at this site.</p>"}</div>
        <h2>Hives</h2>
        <table>
          <thead><tr><th>Hive #</th><th>Status</th><th>Type</th><th>Last Checked</th></tr></thead>
          <tbody>${rows || `<tr><td colspan="4">No hives</td></tr>`}</tbody>
        </table>
      `;

      openPrintWindow(`${site?.dock_name || "Site"} Report`, body, { watermark });
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* No trigger when controlled: the caller supplies the action. */}
      {trigger ? <span onClick={() => setOpen(true)}>{trigger}</span> : null}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Print Site Report"
        footer={<Button onClick={print} disabled={busy || !siteId}>{busy ? "Preparing…" : "Print"}</Button>}
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <label>
            Site
            <select id="print-site-report-site-id" name="site-id" value={siteId} onChange={(e) => setSiteId(e.target.value)} className="touch-target" style={{ display: "block", width: "100%" }}>
              <option value="">Select a site…</option>
              {sites?.map((s) => <option key={s.site_id} value={s.site_id}>{s.dock_name}</option>)}
            </select>
            {sites?.length === 0 && (
              <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>No sites available.</p>
            )}
          </label>
          <WatermarkSelect value={watermark} onChange={setWatermark} />
        </div>
      </Modal>
    </>
  );
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
